// Wraps App's preview ops with a `learn:` id namespace so Learn previews
// never collide with vault previews and can be cleaned up on modal-close
// without touching main playback.
//
// Caller passes the App-level previewId / canPreview / onPlay / onStop
// (sourced from useStrudel + handleStartPreview). This hook returns
// per-example { active, canPlay, play, stop } closures the snippet UI
// can bind directly to a button.

import { useCallback } from 'react';
import type { CodeExample } from './types';

export type LearnPreviewSource = {
  id: string | null;
  canPreview: boolean;
  onPlay: (id: string, code: string, label: string, durationMs?: number) => void;
  onStop: () => void;
};

export type SnippetPreview = {
  active: boolean;
  canPlay: boolean;
  play: () => void;
  stop: () => void;
};

const PREFIX = 'learn:';

/** The single id reserved for the Scratchpad's playback. Lets the modal
 *  know whether the audio engine is currently being driven by the
 *  scratchpad (vs. a snippet preview). */
export const SCRATCH_PREVIEW_ID = `${PREFIX}scratch`;

export function namespaceId(lessonId: string, example: CodeExample): string {
  return `${PREFIX}${lessonId}:${example.id}`;
}

export function isLearnPreviewId(id: string | null): boolean {
  return id !== null && id.startsWith(PREFIX);
}

export function isScratchPreviewId(id: string | null): boolean {
  return id === SCRATCH_PREVIEW_ID;
}

export function useLearnPreview(
  source: LearnPreviewSource,
  lessonId: string,
): (example: CodeExample) => SnippetPreview {
  const { id, canPreview, onPlay, onStop } = source;

  return useCallback(
    (example: CodeExample): SnippetPreview => {
      const fullId = namespaceId(lessonId, example);
      const active = id === fullId;
      return {
        active,
        canPlay: canPreview,
        play: () => onPlay(fullId, example.code, example.label, example.durationMs),
        stop: () => onStop(),
      };
    },
    [id, canPreview, onPlay, onStop, lessonId],
  );
}
